import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  useTheme,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { toast } from "react-toastify";
import axios from "axios";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const CLOUDINARY_IMAGE_URL =
  "https://api.cloudinary.com/v1_1/dcmr4gnq5/image/upload";
const CLOUDINARY_RAW_URL =
  "https://api.cloudinary.com/v1_1/dcmr4gnq5/raw/upload";
const IMAGE_UPLOAD_PRESET = "book_uploads";
const DIGITAL_FILE_UPLOAD_PRESET = "book_digital_files";
const GIVEAWAY_FILE_UPLOAD_PRESET = "book_digital_files";

const AddBook = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isLight = theme.palette.mode === "light";

  const [bookImg, setBookImg] = useState(null);
  const [digitalFile, setDigitalFile] = useState(null);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState("General");
  const [description, setDescription] = useState("");
  const [format, setFormat] = useState("Digital");
  const [uploading, setUploading] = useState(false);
  const [giveawayFile, setGiveawayFile] = useState(null);

  const adminToken = localStorage.getItem("adminToken");

  const validateImageFile = (file) => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type))
      throw new Error("Please select JPEG, PNG, or WebP");
    if (file.size > 10 * 1024 * 1024)
      throw new Error("Image must be under 10MB");
  };

  const validateDigitalFile = (file) => {
    const validTypes = ["application/pdf", "application/epub+zip"];
    if (
      !validTypes.includes(file.type) &&
      !file.name.toLowerCase().endsWith(".epub")
    )
      throw new Error("Please select a PDF or ePub file");
    if (file.size > 50 * 1024 * 1024)
      throw new Error("File must be under 50MB");
  };

  const validateGiveawayFile = (file) => {
    const validTypes = ["application/pdf", "application/epub+zip"];
    if (
      !validTypes.includes(file.type) &&
      !file.name.toLowerCase().endsWith(".epub")
    )
      throw new Error("Please select a PDF or ePub file");
    if (file.size > 50 * 1024 * 1024)
      throw new Error("Giveaway file must be under 50MB");
  };

  const uploadToCloudinary = async (file, preset, isImage) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", preset);
    data.append("cloud_name", "dcmr4gnq5");
    const res = await fetch(
      isImage ? CLOUDINARY_IMAGE_URL : CLOUDINARY_RAW_URL,
      {
        method: "POST",
        body: data,
      },
    );
    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
    const uploaded = await res.json();
    if (uploaded.error) throw new Error(uploaded.error.message);
    if (!uploaded.secure_url) throw new Error("No URL returned");
    return uploaded.secure_url;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      validateImageFile(file);
      setBookImg(file);
    } catch (err) {
      toast.error(err.message);
      e.target.value = "";
    }
  };

  const handleDigitalFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      validateDigitalFile(file);
      setDigitalFile(file);
    } catch (err) {
      toast.error(err.message);
      e.target.value = "";
    }
  };

  const handleGiveawayFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      validateGiveawayFile(file);
      setGiveawayFile(file);
    } catch (err) {
      toast.error(err.message);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading) return;
    if (!bookImg) return toast.error("Please select a book cover image");
    if ((format === "Digital" || format === "Both") && !digitalFile)
      return toast.error("Please upload the digital file");
    if (!title.trim() || !author.trim() || !price || !description.trim())
      return toast.error("Please fill in all required fields");
    if (isNaN(price) || Number(price) <= 0)
      return toast.error("Please enter a valid price");

    try {
      setUploading(true);
      toast.info("Uploading cover image...");
      const coverUrl = await uploadToCloudinary(
        bookImg,
        IMAGE_UPLOAD_PRESET,
        true,
      );

      let digitalUrl = "";
      if (digitalFile) {
        toast.info("Uploading digital file...");
        digitalUrl = await uploadToCloudinary(
          digitalFile,
          DIGITAL_FILE_UPLOAD_PRESET,
          false,
        );
      }

      let giveawayUrl = "";
      if (giveawayFile) {
        toast.info("Uploading giveaway file...");
        giveawayUrl = await uploadToCloudinary(
          giveawayFile,
          GIVEAWAY_FILE_UPLOAD_PRESET,
          false,
        );
      }

      toast.info("Saving book...");
      const { data } = await axios.post(
        BASE_URL + "/api/admin/add-book",
        {
          title: title.trim(),
          author: author.trim(),
          price: Number(price),
          category,
          description: description.trim(),
          format,
          coverImage: coverUrl,
          digitalFile: digitalUrl,
          giveawayFile: giveawayUrl || null,
        },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (data.success) {
        toast.success("Book added successfully!");
        setBookImg(null);
        setDigitalFile(null);
        setGiveawayFile(null);
        setTitle("");
        setAuthor("");
        setPrice("");
        setCategory("General");
        setDescription("");
        setFormat("Digital");
        document.getElementById("book-img-input").value = "";
        const df = document.getElementById("digital-file-input");
        if (df) df.value = "";
        const gf = document.getElementById("giveaway-file-input");
        if (gf) gf.value = "";
      } else {
        toast.error(data.message || "Failed to add book");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "An error occurred",
      );
    } finally {
      setUploading(false);
    }
  };

  const inputBg = isLight ? "#ffffff" : colors.primary[500];
  const inputText = isLight ? "#111827" : colors.grey[100];
  const inputLabel = isLight ? "#4b5563" : colors.grey[300];
  const inputBorder = isLight ? "#d1d5db" : colors.primary[300];
  const cardBg = isLight ? "#f9fafb" : colors.primary[400];
  const cardBorder = isLight ? "#e5e7eb" : colors.primary[300];
  const uploadBg = isLight ? "#ffffff" : colors.primary[500];
  const uploadBorder = isLight ? "#d1d5db" : colors.primary[300];
  const filenameBg = isLight ? "#f0fdf4" : colors.primary[500];
  const filenameBorder = isLight ? "#86efac" : colors.greenAccent[700];
  const filenameText = isLight ? "#166534" : colors.greenAccent[300];

  const cardSx = {
    backgroundColor: cardBg,
    borderRadius: "16px",
    padding: "28px",
    border: `1px solid ${cardBorder}`,
  };

  const sectionLabelSx = {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: colors.greenAccent[isLight ? 500 : 400],
    mb: 3,
    display: "flex",
    alignItems: "center",
    gap: 1.5,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: inputBg,
      "& fieldset": { borderColor: inputBorder },
      "&:hover fieldset": { borderColor: colors.greenAccent[400] },
      "&.Mui-focused fieldset": { borderColor: colors.greenAccent[400] },
    },
    "& .MuiInputLabel-root": { color: inputLabel },
    "& .MuiInputLabel-root.Mui-focused": {
      color: colors.greenAccent[isLight ? 500 : 400],
    },
    "& .MuiInputBase-input": { color: inputText },
    "& .MuiSelect-icon": { color: inputLabel },
    "& .MuiMenuItem-root": { color: inputText },
  };

  return (
    <Box m="20px">
      <Header title="ADD BOOK" subtitle="Add a new book to your catalog" />
      {uploading && (
        <LinearProgress
          sx={{
            mb: 2,
            borderRadius: 2,
            "& .MuiLinearProgress-bar": {
              backgroundColor: colors.greenAccent[400],
            },
            backgroundColor: isLight ? "#d1fae5" : colors.primary[300],
          }}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box
          display="grid"
          gridTemplateColumns="300px 1fr"
          gap="24px"
          alignItems="start"
        >
          {/* LEFT — Cover Upload */}
          <Box sx={cardSx}>
            <Typography sx={sectionLabelSx}>
              <MenuBookIcon sx={{ fontSize: 16 }} />
              Book Cover
            </Typography>

            <label
              htmlFor="book-img-input"
              style={{ cursor: uploading ? "not-allowed" : "pointer" }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "3/4",
                  borderRadius: "12px",
                  border: `2px dashed ${bookImg ? colors.greenAccent[400] : uploadBorder}`,
                  backgroundColor: uploadBg,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: colors.greenAccent[400],
                    backgroundColor: isLight ? "#f0fdf4" : colors.primary[400],
                  },
                }}
              >
                {bookImg ? (
                  <img
                    src={URL.createObjectURL(bookImg)}
                    alt="Cover preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box textAlign="center" p={3}>
                    <Avatar
                      sx={{
                        bgcolor: isLight ? "#d1fae5" : colors.greenAccent[700],
                        width: 56,
                        height: 56,
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <CloudUploadIcon
                        sx={{
                          color: isLight
                            ? colors.greenAccent[600]
                            : colors.greenAccent[300],
                        }}
                      />
                    </Avatar>
                    <Typography
                      fontSize="13px"
                      fontWeight={600}
                      color={isLight ? "#374151" : colors.grey[200]}
                    >
                      Click to upload
                    </Typography>
                    <Typography
                      fontSize="11px"
                      color={isLight ? "#9ca3af" : colors.grey[400]}
                      mt={0.5}
                    >
                      JPEG, PNG, WebP · Max 10MB
                    </Typography>
                  </Box>
                )}
              </Box>
            </label>
            <input
              id="book-img-input"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleImageChange}
              disabled={uploading}
              hidden
            />

            {bookImg && (
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mt={2}
                p={1.5}
                sx={{
                  backgroundColor: filenameBg,
                  borderRadius: "8px",
                  border: `1px solid ${filenameBorder}`,
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: isLight ? "#16a34a" : colors.greenAccent[400],
                    fontSize: 16,
                  }}
                />
                <Typography fontSize="11px" color={filenameText} noWrap>
                  {bookImg.name}
                </Typography>
              </Box>
            )}
          </Box>

          {/* RIGHT — All Fields */}
          <Box display="flex" flexDirection="column" gap="20px">
            {/* Basic Info */}
            <Box sx={cardSx}>
              <Typography sx={sectionLabelSx}>
                <AutoStoriesIcon sx={{ fontSize: 16 }} />
                Book Details
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="16px">
                <TextField
                  label="Title *"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  sx={{ ...inputSx, gridColumn: "span 2" }}
                  disabled={uploading}
                  required
                />
                <TextField
                  label="Author *"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                  required
                />
                <TextField
                  label="Price (KES) *"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  type="number"
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                  required
                  InputProps={{
                    startAdornment: (
                      <Typography color={inputLabel} fontSize="13px" mr={0.5}>
                        KES
                      </Typography>
                    ),
                  }}
                />
                <TextField
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  select
                  fullWidth
                  sx={inputSx}
                  disabled={uploading}
                >
                  {[
                    "General",
                    "Family",
                    "Healing",
                    "Relationships",
                    "Parenting",
                    "Success",
                    "Featured",
                  ].map((c) => (
                    <MenuItem key={c} value={c} sx={{ color: inputText }}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* Format Toggle */}
              <Box mt={2.5}>
                <Typography
                  fontSize="11px"
                  fontWeight={700}
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                  color={inputLabel}
                  mb={1.5}
                >
                  Format
                </Typography>
                <ToggleButtonGroup
                  value={format}
                  exclusive
                  onChange={(_, val) => val && setFormat(val)}
                  disabled={uploading}
                  sx={{
                    "& .MuiToggleButton-root": {
                      color: isLight ? "#374151" : colors.grey[300],
                      borderColor: isLight ? "#d1d5db" : colors.primary[300],
                      borderRadius: "8px !important",
                      px: 3,
                      py: 1,
                      fontSize: "12px",
                      fontWeight: 600,
                      textTransform: "none",
                      mx: 0.5,
                      "&.Mui-selected": {
                        backgroundColor:
                          colors.greenAccent[isLight ? 600 : 700],
                        color: isLight ? "#ffffff" : colors.greenAccent[100],
                        borderColor: colors.greenAccent[500],
                        "&:hover": { backgroundColor: colors.greenAccent[500] },
                      },
                      "&:hover": {
                        backgroundColor: isLight
                          ? "#f0fdf4"
                          : colors.primary[300],
                      },
                    },
                  }}
                >
                  <ToggleButton value="Digital">Digital</ToggleButton>
                  <ToggleButton value="Print">Print</ToggleButton>
                  <ToggleButton value="Both">Digital + Print</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            {/* Description */}
            <Box sx={cardSx}>
              <Typography sx={sectionLabelSx}>Description</Typography>
              <TextField
                label="About the Book *"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={5}
                fullWidth
                sx={inputSx}
                disabled={uploading}
                required
              />
              <Typography
                fontSize="11px"
                color={isLight ? "#9ca3af" : colors.grey[400]}
                textAlign="right"
                mt={1}
              >
                {description.length} characters
              </Typography>
            </Box>

            {/* Digital + Giveaway — side by side when digital format is selected */}
            {format === "Digital" || format === "Both" ? (
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="20px">
                {/* Digital File */}
                <Box sx={cardSx}>
                  <Typography sx={sectionLabelSx}>
                    <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                    Digital File
                    <span
                      style={{
                        fontWeight: 500,
                        textTransform: "none",
                        fontSize: "10px",
                        color: isLight ? "#dc2626" : "#f87171",
                        letterSpacing: "0.04em",
                      }}
                    >
                      Required
                    </span>
                  </Typography>
                  <label
                    htmlFor="digital-file-input"
                    style={{ cursor: uploading ? "not-allowed" : "pointer" }}
                  >
                    <Box
                      sx={{
                        border: `2px dashed ${digitalFile ? colors.greenAccent[400] : uploadBorder}`,
                        borderRadius: "12px",
                        p: 3,
                        textAlign: "center",
                        backgroundColor: uploadBg,
                        transition: "all 0.2s ease",
                        minHeight: "110px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: colors.greenAccent[400],
                          backgroundColor: isLight
                            ? "#f0fdf4"
                            : colors.primary[400],
                        },
                      }}
                    >
                      {digitalFile ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          justifyContent="center"
                          width="100%"
                        >
                          <Avatar
                            sx={{
                              bgcolor: isLight ? "#fee2e2" : "#b71c1c",
                              width: 40,
                              height: 40,
                              flexShrink: 0,
                            }}
                          >
                            <PictureAsPdfIcon
                              fontSize="small"
                              sx={{ color: isLight ? "#dc2626" : "#fff" }}
                            />
                          </Avatar>
                          <Box textAlign="left" sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              fontSize="13px"
                              fontWeight={600}
                              color={isLight ? "#111827" : colors.grey[100]}
                              noWrap
                            >
                              {digitalFile.name}
                            </Typography>
                            <Typography
                              fontSize="11px"
                              color={isLight ? "#6b7280" : colors.grey[400]}
                            >
                              {(digitalFile.size / 1024 / 1024).toFixed(2)} MB ·
                              Click to change
                            </Typography>
                          </Box>
                          <CheckCircleIcon
                            sx={{
                              color: isLight
                                ? "#16a34a"
                                : colors.greenAccent[400],
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <Avatar
                            sx={{
                              bgcolor: isLight
                                ? "#f3f4f6"
                                : colors.primary[300],
                              width: 48,
                              height: 48,
                              mx: "auto",
                              mb: 1.5,
                            }}
                          >
                            <CloudUploadIcon
                              sx={{
                                color: isLight ? "#6b7280" : colors.grey[300],
                              }}
                            />
                          </Avatar>
                          <Typography
                            fontSize="13px"
                            fontWeight={600}
                            color={isLight ? "#374151" : colors.grey[200]}
                          >
                            Upload PDF or ePub
                          </Typography>
                          <Typography
                            fontSize="11px"
                            color={isLight ? "#9ca3af" : colors.grey[400]}
                            mt={0.5}
                          >
                            Maximum 50MB
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </label>
                  <input
                    id="digital-file-input"
                    type="file"
                    accept=".pdf,.epub,application/pdf,application/epub+zip"
                    onChange={handleDigitalFileChange}
                    disabled={uploading}
                    hidden
                  />
                </Box>

                {/* Giveaway File */}
                <Box sx={cardSx}>
                  <Typography sx={sectionLabelSx}>
                    <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                    Free Giveaway PDF
                    <span
                      style={{
                        fontWeight: 500,
                        textTransform: "none",
                        fontSize: "10px",
                        color: isLight ? "#9ca3af" : colors.grey[400],
                        letterSpacing: "0.04em",
                      }}
                    >
                      Optional
                    </span>
                  </Typography>
                  <label
                    htmlFor="giveaway-file-input"
                    style={{ cursor: uploading ? "not-allowed" : "pointer" }}
                  >
                    <Box
                      sx={{
                        border: `2px dashed ${giveawayFile ? colors.greenAccent[400] : uploadBorder}`,
                        borderRadius: "12px",
                        p: 3,
                        textAlign: "center",
                        backgroundColor: uploadBg,
                        transition: "all 0.2s ease",
                        minHeight: "110px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                          borderColor: colors.greenAccent[400],
                          backgroundColor: isLight
                            ? "#fefce8"
                            : colors.primary[400],
                        },
                      }}
                    >
                      {giveawayFile ? (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          justifyContent="center"
                          width="100%"
                        >
                          <Avatar
                            sx={{
                              bgcolor: isLight ? "#fef3c7" : "#78350f",
                              width: 40,
                              height: 40,
                              flexShrink: 0,
                            }}
                          >
                            <PictureAsPdfIcon
                              fontSize="small"
                              sx={{
                                color: isLight ? "#d97706" : "#fcd34d",
                              }}
                            />
                          </Avatar>
                          <Box textAlign="left" sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              fontSize="13px"
                              fontWeight={600}
                              color={isLight ? "#111827" : colors.grey[100]}
                              noWrap
                            >
                              {giveawayFile.name}
                            </Typography>
                            <Typography
                              fontSize="11px"
                              color={isLight ? "#6b7280" : colors.grey[400]}
                            >
                              {(giveawayFile.size / 1024 / 1024).toFixed(2)} MB
                              · Click to change
                            </Typography>
                          </Box>
                          <CheckCircleIcon
                            sx={{
                              color: isLight
                                ? "#16a34a"
                                : colors.greenAccent[400],
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <Avatar
                            sx={{
                              bgcolor: isLight
                                ? "#fef9c3"
                                : colors.primary[300],
                              width: 48,
                              height: 48,
                              mx: "auto",
                              mb: 1.5,
                            }}
                          >
                            <CloudUploadIcon
                              sx={{
                                color: isLight ? "#ca8a04" : colors.grey[300],
                              }}
                            />
                          </Avatar>
                          <Typography
                            fontSize="13px"
                            fontWeight={600}
                            color={isLight ? "#374151" : colors.grey[200]}
                          >
                            Upload Free Summary PDF
                          </Typography>
                          <Typography
                            fontSize="11px"
                            color={isLight ? "#9ca3af" : colors.grey[400]}
                            mt={0.5}
                          >
                            Maximum 50MB
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </label>
                  <input
                    id="giveaway-file-input"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleGiveawayFileChange}
                    disabled={uploading}
                    hidden
                  />
                </Box>
              </Box>
            ) : (
              /* Print-only: giveaway standalone full width */
              <Box sx={cardSx}>
                <Typography sx={sectionLabelSx}>
                  <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                  Free Giveaway PDF
                  <span
                    style={{
                      fontWeight: 500,
                      textTransform: "none",
                      fontSize: "10px",
                      color: isLight ? "#9ca3af" : colors.grey[400],
                      letterSpacing: "0.04em",
                    }}
                  >
                    Optional
                  </span>
                </Typography>
                <label
                  htmlFor="giveaway-file-input"
                  style={{ cursor: uploading ? "not-allowed" : "pointer" }}
                >
                  <Box
                    sx={{
                      border: `2px dashed ${giveawayFile ? colors.greenAccent[400] : uploadBorder}`,
                      borderRadius: "12px",
                      p: 3,
                      textAlign: "center",
                      backgroundColor: uploadBg,
                      transition: "all 0.2s ease",
                      minHeight: "110px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        borderColor: colors.greenAccent[400],
                        backgroundColor: isLight
                          ? "#fefce8"
                          : colors.primary[400],
                      },
                    }}
                  >
                    {giveawayFile ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        justifyContent="center"
                        width="100%"
                      >
                        <Avatar
                          sx={{
                            bgcolor: isLight ? "#fef3c7" : "#78350f",
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                          }}
                        >
                          <PictureAsPdfIcon
                            fontSize="small"
                            sx={{ color: isLight ? "#d97706" : "#fcd34d" }}
                          />
                        </Avatar>
                        <Box textAlign="left" sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            fontSize="13px"
                            fontWeight={600}
                            color={isLight ? "#111827" : colors.grey[100]}
                            noWrap
                          >
                            {giveawayFile.name}
                          </Typography>
                          <Typography
                            fontSize="11px"
                            color={isLight ? "#6b7280" : colors.grey[400]}
                          >
                            {(giveawayFile.size / 1024 / 1024).toFixed(2)} MB ·
                            Click to change
                          </Typography>
                        </Box>
                        <CheckCircleIcon
                          sx={{
                            color: isLight
                              ? "#16a34a"
                              : colors.greenAccent[400],
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    ) : (
                      <Box>
                        <Avatar
                          sx={{
                            bgcolor: isLight ? "#fef9c3" : colors.primary[300],
                            width: 48,
                            height: 48,
                            mx: "auto",
                            mb: 1.5,
                          }}
                        >
                          <CloudUploadIcon
                            sx={{
                              color: isLight ? "#ca8a04" : colors.grey[300],
                            }}
                          />
                        </Avatar>
                        <Typography
                          fontSize="13px"
                          fontWeight={600}
                          color={isLight ? "#374151" : colors.grey[200]}
                        >
                          Upload Free Summary PDF
                        </Typography>
                        <Typography
                          fontSize="11px"
                          color={isLight ? "#9ca3af" : colors.grey[400]}
                          mt={0.5}
                        >
                          Maximum 50MB
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </label>
                <input
                  id="giveaway-file-input"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleGiveawayFileChange}
                  disabled={uploading}
                  hidden
                />
              </Box>
            )}

            {/* Submit */}
            <Button
              type="submit"
              variant="contained"
              disabled={uploading}
              startIcon={uploading ? null : <AddIcon />}
              fullWidth
              sx={{
                backgroundColor: colors.greenAccent[isLight ? 500 : 600],
                color: isLight ? "#ffffff" : colors.primary[900],
                fontWeight: 700,
                fontSize: "14px",
                py: 1.8,
                borderRadius: "12px",
                textTransform: "none",
                letterSpacing: "0.05em",
                "&:hover": {
                  backgroundColor: colors.greenAccent[isLight ? 400 : 500],
                  boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                },
                "&:disabled": {
                  backgroundColor: isLight ? "#d1d5db" : colors.primary[300],
                  color: isLight ? "#9ca3af" : colors.grey[500],
                },
              }}
            >
              {uploading ? "Adding Book..." : "Add Book to Catalog"}
            </Button>
          </Box>
        </Box>
      </form>
    </Box>
  );
};

export default AddBook;
